import type {MouseEventHandler, ReactElement} from 'react';
import React, { useRef} from 'react';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {FontDeleteButton} from '@Components/font-selector-panel/components/font-delete-button';
import {CircularProgressLoader} from '@Components/circular-progress-loader';
import type {ContainerQueryObject} from '@Hooks/useContainerQuery';
import { useContainerQuery} from '@Hooks/useContainerQuery';
import {getWriteBucket} from '@Utils/s3.util';
import styles from './font-item.module.scss';

interface FontItemProps {
  id?: string;
  className?: string;
  fontName: string;
  isSelected?: boolean;
  isLoading?: boolean;
  onClick?: MouseEventHandler;
  showDeleteActions?: boolean;
  onDelete?: MouseEventHandler;
}

const query: ContainerQueryObject = {
  small: {
    minWidth: 0,
    maxWidth: 155,
  },
  large: {
    minWidth: 156,
  },
};

export function FontItem({
  id = '',
  className = '',
  isSelected = false,
  isLoading = false,
  onClick = (): void => {},
  onDelete = (): void => {},
  showDeleteActions = false,
  ...props
}: FontItemProps): ReactElement<any> {
  const containerRef = useRef<HTMLImageElement>(null);
  const params = useContainerQuery(containerRef, query);

  const getShortFontImage = (): string => {
    // SelfCodeReviewAhmed: there is an issue with the short font image for these fonts
    // this needs to be fixed
    if (props.fontName === 'HanWangKanTan' || props.fontName === 'StintUltraCondensed') {
      return getNormalFontImage();
    }
    return window.PMW.util.repoURL(`fonts/${props.fontName}_short.png?`, getWriteBucket()) as string;
  };

  const getNormalFontImage = (): string => {
    return window.PMW.util.repoURL(`fonts/${props.fontName}.png?`, getWriteBucket()) as string;
  };

  return (
    <li className="flexbox spacing-m-t-1 spacing-m-b-1">
      <div
        id={id}
        onClick={onClick}
        className={`${styles.fontItem} radius-4 flex-row-justify-between flex-items-center hover-transition-all spacing-p-2 ${isSelected ? styles.selectedItem : ''} ${className}`}
      >
        <img
          ref={containerRef}
          className={`${styles.fontImage} ${isSelected ? styles.selectedFontImage : ''} ${showDeleteActions || isSelected ? styles.imageWithActions : styles.fullImage}`}
          src={`${params.small ? getShortFontImage() : getNormalFontImage()}`}
          alt=""
        />
        {isLoading && isSelected ? <CircularProgressLoader /> : null}
        {isSelected && !isLoading ? <Icon icon="icon-check" size={IconSize.SIZE_ICON_24} /> : null}
      </div>
      {showDeleteActions ? <FontDeleteButton onDeleteConfirm={onDelete} /> : null}
    </li>
  );
}
